import React, { useState } from 'react';
import { X, User, Calendar, Star, Send } from 'lucide-react';
import { backendUrl } from '../utils/socketClient';

interface ReviewModalProps {
  review: any;
  storeName: string;
  userEmail: string;
  onClose: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ review, storeName, userEmail, onClose }) => {
  const [replyContent, setReplyContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  const generateReply = async () => {
    if (!review.reviewcontent) {
      alert("口コミの内容がありません");
      return;
    }

    setIsGenerating(true);
    setReplyContent("返信生成中...");
    setLimitReached(false);

    try {
      const response = await fetch(`${backendUrl}/generate_reply/${encodeURIComponent(storeName)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          review_content: review.reviewcontent,
          star_rating: review.starrating,
          reviewer_name: review.username,
          user_email: userEmail
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.limit_reached) {
          setLimitReached(true);
          setReplyContent("");
        }
        throw new Error(data.error || "HTTP error " + response.status);
      }

      if (data.reply) {
        setReplyContent(data.reply);
      } else if (data.error) {
        setReplyContent("返信生成エラー: " + data.error);
      }
    } catch (error: any) {
      console.error("返信生成エラー:", error);
      if (!limitReached) {
        setReplyContent("返信生成中にエラーが発生しました: " + error.message);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star 
          key={i} 
          className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
        />
      );
    }
    return <div className="flex">{stars}</div>;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Modal Header */}
          <div className="bg-[#007AFF] px-4 py-3 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-white" id="modal-title">
              口コミ詳細
            </h3>
            <button
              type="button"
              className="text-white hover:text-gray-200 focus:outline-none"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Review Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-md flex flex-wrap gap-4 items-center">
              <div className="flex items-center">
                <User className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700">{review.username || "匿名"}</span>
              </div>
              <div className="flex items-center">
                {review.starrating && renderStars(parseInt(review.starrating))}
              </div>
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-700">{review.reviewdate || ""}</span>
              </div>
            </div>

            {/* Review Content and Reply */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  口コミ内容
                </label>
                <div className="border border-gray-300 rounded-md p-3 bg-gray-50 h-64 overflow-y-auto">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{review.reviewcontent || ""}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  返信内容
                </label>
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  className="shadow-sm focus:ring-[#007AFF] focus:border-[#007AFF] block w-full sm:text-sm border-gray-300 rounded-md h-64 p-3"
                  placeholder="ここに返信内容が表示されます"
                />
              </div>
            </div>

            {/* 上限到達メッセージ */}
            {limitReached && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 text-sm font-medium">
                  今月の生成上限に達しました。プランをアップグレードしてください。
                </p>
                <a
                  href={import.meta.env.VITE_STRIPE_CUSTOMER_PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  プラン変更
                </a>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={generateReply}
              disabled={isGenerating}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#007AFF] text-base font-medium text-white hover:bg-[#007AFF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  生成中...
                </span>
              ) : (
                <span className="flex items-center">
                  <Send className="mr-2 h-4 w-4" />
                  返信を生成
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
